import depsloader from "../../../depsloader";

function swGetGeocoderConfig() {
  const c = (typeof window !== "undefined" && window.SW_GEOCODER) ? window.SW_GEOCODER : {};
  return {
    url: c.geocoderUrl || "https://api.screenwork.de/geocoder/search",
    reverseUrl: c.geocoderReverseUrl || "https://api.screenwork.de/geocoder/reverse",
    key: c.geocoderKey || ""
  };
}

class Geocoder {
  constructor(axios) {
    this.axios = axios;
    this.axiosRequest = null;
    this._seq = 0;
  }

  cancelAutocomplete() {
    this._seq++;
    if (this.axiosRequest != null) {
      this.axiosRequest.source.cancel();
      this.axiosRequest = null;
    }
  }

  _geoaddressText(g) {
    let s = "";
    if (g.zip && g.city && g.district) {
      s = g.zip + " " + g.city + "–" + g.district;
    } else if (g.zip && g.city) {
      s = g.zip + " " + g.city;
    } else if (g.city && g.district) {
      s = g.city + "–" + g.district;
    } else if (g.city) {
      s = g.city;
    } else if (g.name) {
      s = g.name;
    }

    const p = [];

    if (g.street && g.number) {
      p.push(g.street + " " + g.number);
    } else if (g.street) {
      p.push(g.street);
    }

    if (s) {
      p.push(s);
    }

    if (g.state) {
      p.push(g.state);
    }

    if (g.country) {
      p.push(g.country);
    }

    return p.join(", ");
  }

  _geoaddress(l) {
    const geoaddress = {};
    const p = (l && l.properties) ? l.properties : {};
    const type = p.type || null;

    // Legacy-Fallback: Photon lieferte Straßen als osm_key "highway".
    if (!p.street && p.osm_key === "highway" && p.name) {
      p.street = p.name;
    }

    // Straße / Hausnummer
    if (p.street && p.housenumber) {
      geoaddress["street"] = p.street;
      geoaddress["number"] = p.housenumber;
    } else if (p.street) {
      geoaddress["street"] = p.street;
    } else if (type === "street" && p.name) {
      geoaddress["street"] = p.name;
    }

    // PLZ
    if (p.postcode) {
      geoaddress["zip"] = p.postcode;
    }

    // Stadt / Bezirk – properties.type und district vorrangig, osm_* als Fallback.
    const isDistrictType =
      type === "district" ||
      type === "locality" ||
      p.osm_key === "boundary" ||
      (p.osm_key === "place" && p.osm_value === "suburb");

    if (p.city) {
      geoaddress["city"] = p.city;
      if (p.district && p.district !== p.city) {
        geoaddress["district"] = p.district;
      } else if (isDistrictType && p.name && p.name !== p.city) {
        geoaddress["district"] = p.name;
      }
    } else if (type === "city" && p.name) {
      geoaddress["city"] = p.name;
    } else if (p.district) {
      geoaddress["city"] = p.district;
    } else if (p.name) {
      // Keine Stadt bekannt: Feature-Namen als Beschriftung erhalten, nicht als Stadt erfinden.
      geoaddress["name"] = p.name;
    }

    // Koordinaten bleiben [lng, lat].
    if (l && l.geometry && Array.isArray(l.geometry.coordinates)) {
      geoaddress["lng"] = l.geometry.coordinates[0];
      geoaddress["lat"] = l.geometry.coordinates[1];
    }

    if (p.state) {
      geoaddress["state"] = p.state;
    }
    if (p.country) {
      geoaddress["country"] = p.country;
    }

    geoaddress["text"] = this._geoaddressText(geoaddress);

    return geoaddress;
  }

  geocode(params) {
    const cfg = swGetGeocoderConfig();
    return new Promise((resolve, reject) => {
      if (!cfg.key) {
        // Ohne Key kontrolliert ohne Anfrage enden.
        this.axiosRequest = null;
        resolve([]);
        return;
      }

      params.append("key", cfg.key);
      const seq = ++this._seq;

      this.axios.get(cfg.url, {
        cancelToken: this.axiosRequest.source.token,
        timeout: 25000,
        params
      }).then(res => {
        if (seq !== this._seq) {
          return; // veraltete Antwort verwerfen
        }
        const results = [];
        if (res && res.data && Array.isArray(res.data.features)) {
          res.data.features.forEach(l => {
            const geoaddress = this._geoaddress(l);
            if (geoaddress) {
              results.push(geoaddress);
            }
          });
        }
        resolve(results);
      }).catch(thrown => {
        if (this.axios.isCancel && this.axios.isCancel(thrown)) {
          return;
        }
        if (seq !== this._seq) {
          return;
        }
        console.log(thrown);
        reject(thrown);
      }).finally(() => {
        if (seq === this._seq) {
          this.axiosRequest = null;
        }
      });
    });
  }

  autocomplete(q) {
    const CancelToken = this.axios.CancelToken;
    this.axiosRequest = {
      source: CancelToken.source()
    };

    const params = new URLSearchParams();
    params.append("q", q);
    params.append("lang", "de");
    params.append("limit", 10);
    params.append("osm_tag", "!place:state");
    params.append("osm_tag", "!place:house");
    //params.append("osm_tag", "!boundary:protected_area");
    params.append("osm_tag", "place");
    params.append("osm_tag", "boundary");

    return this.geocode(params);
  }

  reverse(lat, lng) {
    const cfg = swGetGeocoderConfig();
    return new Promise((resolve, reject) => {
      if (!cfg.key) {
        reject(new Error("no maps key"));
        return;
      }

      const latNum = Number(lat);
      const lonNum = Number(lng);
      if (
        !isFinite(latNum) || !isFinite(lonNum) ||
        latNum < -90 || latNum > 90 ||
        lonNum < -180 || lonNum > 180
      ) {
        reject(new Error("invalid coordinates"));
        return;
      }

      this.axios.get(cfg.reverseUrl, {
        timeout: 25000,
        params: {
          lat: latNum,
          lon: lonNum,
          lang: "de",
          key: cfg.key
        }
      }).then(res => {
        if (res && res.data && res.data.features && res.data.features.length > 0) {
          resolve(this._geoaddress(res.data.features[0]));
          return;
        }
        reject(new Error("invalid data"));
      }).catch(err => {
        reject(err);
      });
    });
  }
}

function GeocoderFactory() {
  return new Promise(resolve => {
    depsloader.load("axios").then(axios => {
      resolve(new Geocoder(axios));
    })
  });
}

export { GeocoderFactory };

export default {};
